import { useCallback } from 'react';

import { useTranslation } from '@/i18n';
import { scheduleService } from '@/api/domains/schedule';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { ScheduleFilters } from './useScheduleList';


export interface UseScheduleExportParams {
  searchText?: string;
  filters: ScheduleFilters;
}

export interface UseScheduleExportResult {
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
}

export function useScheduleExport(
  { searchText, filters }: UseScheduleExportParams,
): UseScheduleExportResult {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => scheduleService.export({
      searchText: searchText || undefined,
      timeZoneId: filters.timeZoneId,
      isTwoDaySchedule: filters.isTwoDaySchedule,
    }),
    successMessage: translate('schedule:messages.downloadSuccess'),
    errorMessage: translate('schedule:messages.downloadError'),
  });

  const onExport = useCallback(async (format: UIExportFormat) => {
    if (format !== 'EXCEL') {
      showNotification('error', translate('schedule:messages.downloadError'));
      return;
    }
    await exportFile();
  }, [exportFile, showNotification, translate]);

  return { isExporting, onExport };
}
